import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Shield, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from '@/integrations/supabase/client';
import { BrazzaBoostLogo } from "./BrazzaBoostLogo";

export const Header = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    checkAdmin();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      checkAdmin();
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkAdmin = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: roles } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'admin')
        .maybeSingle();
      setIsAdmin(!!roles);
    } else {
      setIsAdmin(false);
    }
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-white/95 backdrop-blur-sm border-b border-slate-200">
      <div className="container flex h-16 items-center justify-between px-6 lg:px-12">
        <Link to="/" className="flex items-center">
          <BrazzaBoostLogo />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-6">
          <Link
            to="/opportunities"
            className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
          >
            Opportunités
          </Link>
          <Link
            to="/blog"
            className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
          >
            Blog
          </Link>
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate("/cv-builder")}
            className="flex items-center gap-2 border-slate-300 text-slate-700 hover:bg-slate-100"
          >
            <Sparkles className="h-4 w-4" />
            <span>Créer mon CV</span>
          </Button>
          
          {isAdmin ? (
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate("/admin")}
              className="text-slate-400 hover:text-slate-600"
            >
              <Shield className="h-4 w-4" />
            </Button>
          ) : (
            <button
              onClick={() => navigate("/auth")}
              className="w-2 h-2 rounded-full bg-slate-800 hover:bg-slate-600 transition-colors"
              aria-label="Admin login"
            />
          )}
          
          <Button 
            onClick={() => navigate("/publish")}
            className="bg-honor-orange hover:bg-honor-orange/90 text-white font-medium"
          >
            Publier une offre
          </Button>
        </nav>

        {/* Mobile menu button */}
        <button
          className="md:hidden p-2"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden border-t border-slate-200 bg-white">
          <nav className="container py-4 px-6 space-y-3">
            <Link
              to="/opportunities"
              className="block py-2 text-sm font-medium text-slate-700 hover:text-honor-orange"
              onClick={() => setIsMenuOpen(false)}
            >
              Opportunités
            </Link>
            <Link
              to="/blog"
              className="block py-2 text-sm font-medium text-slate-700 hover:text-honor-orange"
              onClick={() => setIsMenuOpen(false)}
            >
              Blog
            </Link>
            
            <Button 
              variant="outline"
              className="w-full justify-start border-slate-300" 
              onClick={() => {
                navigate("/cv-builder");
                setIsMenuOpen(false);
              }}
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Créer mon CV
            </Button>
            
            <Button 
              className="w-full bg-honor-orange hover:bg-honor-orange/90" 
              onClick={() => {
                navigate("/publish");
                setIsMenuOpen(false);
              }}
            >
              Publier une offre
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};
